import dayjs from 'dayjs'
import ignore from 'ignore'
import untar from 'js-untar'
import pako from 'pako'
import { getDeviceId } from '~/utils/storage'
import { TarWriter } from './tarjs'
import { getMandatoryExceptionsString } from './uploadIgnoreRules'

/**
 * Convert a string to kebab-case
 * "Project Folder" -> "project-folder"
 * "My App Name" -> "my-app-name"
 */
export function toKebabCase(str: string): string {
  return str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Remove non-alphanumeric characters except hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Extract the original filename from an S3 path that may have device ID prefix.
 * Handles pattern: "deviceId-timestamp___originalName.ext" -> "originalName.ext"
 * Also handles full S3 paths by extracting the basename first.
 *
 * @param pathOrFilename - S3 path or filename, possibly with device ID prefix
 * @returns The original filename without device ID prefix
 */
export function extractOriginalFilename(pathOrFilename: string): string {
  // Get basename if it's a path
  const basename = pathOrFilename.split('/').pop() || pathOrFilename
  // Extract original filename after device ID prefix
  if (basename.includes('___')) {
    return basename.split('___').pop() || basename
  }
  return basename
}

export interface ExtractedFile {
  /** File path with kebab-case base prefix, e.g., "project-folder/src/index.ts" */
  filePath: string
  /** File content as string (text) or raw base64 (binary) */
  content: string
  /** Whether the content is base64 encoded (binary file) */
  isBase64: boolean
  /** Detected mime type based on extension */
  mimeType: string
}

/**
 * Binary file extensions that should be treated as base64.
 *
 * Office documents (`docx`, `xlsx`, `pptx`, etc.) are zipped XML archives —
 * fetching them as text yields garbled mojibake (`PK`, `[Content_Types].xml`,
 * binary bytes) and the in-app viewer used to dump that into a code block.
 * Treat them as base64 so the viewer routes them through the binary fallback
 * ("Use the Download button to save this file") instead. (CUR-176)
 */
export const BINARY_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'ico',
  'svg',
  'pdf',
  'zip',
  'gz',
  'tar',
  'rar',
  '7z',
  'woff',
  'woff2',
  'ttf',
  'eot',
  'otf',
  'mp3',
  'mp4',
  'wav',
  'avi',
  'mov',
  'mkv',
  'webm',
  'exe',
  'dll',
  'so',
  'dylib',
  'class',
  'pyc',
  'pyo',
  'sqlite',
  'db',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx'
])

/**
 * Check if a file is binary based on its extension
 */
export function isBinaryFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  return BINARY_EXTENSIONS.has(ext)
}

/**
 * Get MIME type from filename extension
 */
function getMimeTypeFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  const mimeTypes: Record<string, string> = {
    // Text
    txt: 'text/plain',
    md: 'text/markdown',
    html: 'text/html',
    css: 'text/css',
    csv: 'text/csv',
    // Code
    js: 'text/javascript',
    jsx: 'text/javascript',
    ts: 'text/typescript',
    tsx: 'text/typescript',
    json: 'application/json',
    xml: 'application/xml',
    yaml: 'text/yaml',
    yml: 'text/yaml',
    py: 'text/x-python',
    java: 'text/x-java',
    go: 'text/x-go',
    rs: 'text/x-rust',
    c: 'text/x-c',
    cpp: 'text/x-c++',
    h: 'text/x-c',
    hpp: 'text/x-c++',
    sh: 'text/x-shellscript',
    bash: 'text/x-shellscript',
    sql: 'text/x-sql',
    // Images
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    ico: 'image/x-icon',
    bmp: 'image/bmp',
    // Documents
    pdf: 'application/pdf',
    // Archives
    zip: 'application/zip',
    gz: 'application/gzip',
    tar: 'application/x-tar'
  }
  return mimeTypes[ext] || 'application/octet-stream'
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Check if a tar entry should be skipped (directories, metadata, macOS files)
 */
function shouldSkipTarEntry(name: string, type: string): boolean {
  // Skip directories (type '5')
  if (type === '5' || name.endsWith('/')) {
    return true
  }
  // Skip PAX headers and GNU long names
  if (type === 'x' || type === 'g' || type === 'L') {
    return true
  }
  // Skip macOS resource fork files
  if (name.startsWith('./._') || name.startsWith('._') || name.includes('/._')) {
    return true
  }
  return false
}

/**
 * Extract files from a tar.gz archive using js-untar library
 *
 * @param base64Content - The tar.gz file content as base64 string
 * @param title - The title to convert to kebab-case for the base directory prefix
 * @returns Array of extracted files with paths prefixed by kebab-case title
 */
export async function extractTarGz(base64Content: string, title: string): Promise<ExtractedFile[]> {
  // Validate base64 content
  if (!base64Content || base64Content.length === 0) {
    throw new Error('Empty base64 content provided')
  }

  // Convert base64 to ArrayBuffer
  const compressedData = base64ToArrayBuffer(base64Content)

  // Decompress gzip using pako
  const tarData = pako.ungzip(new Uint8Array(compressedData))

  // Parse tar file using js-untar
  const files = await untar(tarData.buffer as ArrayBuffer)

  // Convert title to kebab-case for the base prefix
  const basePrefix = toKebabCase(title)

  const extractedFiles: ExtractedFile[] = []

  for (const file of files) {
    // Skip directories, PAX headers, and macOS metadata files
    if (shouldSkipTarEntry(file.name, file.type)) {
      continue
    }

    // Skip empty files
    if (!file.buffer || file.buffer.byteLength === 0) {
      continue
    }

    // Get the file path, removing any existing root folder prefix
    // e.g., "original-folder/src/index.ts" -> "src/index.ts"
    const pathParts = file.name.split('/')
    const relativePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : file.name

    // Skip if relative path is empty (was just a root folder)
    if (!relativePath) {
      continue
    }

    // Create the new file path with kebab-case base prefix
    const filePath = `${basePrefix}/${relativePath}`

    // Get file content
    const isBinary = isBinaryFile(file.name)
    let content: string

    if (isBinary) {
      content = arrayBufferToBase64(file.buffer)
    } else {
      // Avoid file.readAsString() — js-untar uses
      // String.fromCharCode.apply(null, charCodes) which blows the
      // call stack for files > ~65 KB.
      content = new TextDecoder('utf-8').decode(file.buffer)
    }

    extractedFiles.push({
      filePath,
      content,
      isBase64: isBinary,
      mimeType: getMimeTypeFromFilename(file.name)
    })
  }

  return extractedFiles
}

// Memory storage for tar blobs
let lastTarBlob: Blob | null = null
let lastTarFilename: string | null = null
let lastGzipBlob: Blob | null = null
let lastGzipFilename: string | null = null

/**
 * Store tarBlob in memory for later download
 */
export function storeTarBlob(tarBlob: Blob, filename: string): void {
  lastTarBlob = tarBlob
  lastTarFilename = filename
}

/**
 * Store gzipped tar blob in memory for later download
 */
export function storeGzipBlob(gzipBlob: Blob, filename: string): void {
  lastGzipBlob = gzipBlob
  lastGzipFilename = filename
}

/**
 * Download the last uploaded tar blob
 */
export function downloadLastUpload(): void {
  if (!lastTarBlob || !lastTarFilename) {
    console.warn('No tar blob stored in memory to download')
    return
  }

  const url = window.URL.createObjectURL(lastTarBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = lastTarFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Download the last uploaded gzipped tar blob
 */
export function downloadLastUploadGzip(): void {
  if (!lastGzipBlob || !lastGzipFilename) {
    console.warn('No gzipped tar blob stored in memory to download')
    return
  }

  const url = window.URL.createObjectURL(lastGzipBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = lastGzipFilename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Clear stored tar blob from memory
 */
export function clearStoredTarBlob(): void {
  lastTarBlob = null
  lastTarFilename = null
  lastGzipBlob = null
  lastGzipFilename = null
}

/**
 * Read .gitignore content from the uploaded files if it exists
 */
async function readGitIgnoreFromFiles(
  files: File[],
  rootFolder: string,
  isDropped: boolean
): Promise<string> {
  // Find .gitignore file in the root of the uploaded folder
  const gitIgnoreFile = files.find(file => {
    const filePath = isDropped
      ? file.webkitRelativePath.split('/').slice(1).join('/')
      : file.webkitRelativePath
    const relativePath = filePath.replace(`${rootFolder}/`, '')
    return relativePath === '.gitignore'
  })

  if (!gitIgnoreFile) {
    return ''
  }

  try {
    const content = await gitIgnoreFile.text()
    return content
  } catch (error) {
    console.warn('Failed to read .gitignore from uploaded files:', error)
    return ''
  }
}

/**
 * Create compressed tar archive from file list
 *
 * Filtering logic:
 * 1. First, read .gitignore from the uploaded repository (if exists)
 * 2. Then, apply mandatory exception rules (hidden folders, build outputs, etc.)
 */
export async function createCompressedTarArchive(
  files: FileList,
  _gitIgnoreRules: string, // Kept for backward compatibility, but now we read from uploaded repo
  isDropped: boolean = false,
  onProgress?: (stage: string, current: number, total: number) => void
): Promise<{ tarFile: File; tarBlob: Blob; originalFilename: string }> {
  // Validate inputs
  if (!files || files.length === 0) {
    throw new Error('No files provided for upload')
  }

  const tarWriter = new TarWriter()
  const fileArray = Array.from(files)

  // Check if files have the required webkitRelativePath property
  if (!fileArray[0].webkitRelativePath) {
    throw new Error('Files must be selected via folder selection (not individual file selection)')
  }

  const rootFolder = fileArray[0].webkitRelativePath.split('/')[0]
  if (!rootFolder) {
    throw new Error('Could not determine root folder name')
  }

  // Step 1: Read .gitignore from the uploaded repository
  const repoGitIgnore = await readGitIgnoreFromFiles(fileArray, rootFolder, isDropped)

  // Step 2: Get mandatory exception rules
  const mandatoryExceptions = getMandatoryExceptionsString()

  // Step 3: Combine rules - repo's .gitignore first, then mandatory exceptions
  const combinedRules = [repoGitIgnore, mandatoryExceptions].filter(Boolean).join('\n')

  const ig = ignore({ ignoreCase: false }).add(combinedRules)

  // Pre-filter files to avoid unnecessary processing
  const filesToProcess = fileArray.filter(file => {
    const filePath = isDropped
      ? file.webkitRelativePath.split('/').slice(1).join('/')
      : file.webkitRelativePath
    const relativePath = filePath.replace(`${rootFolder}/`, '')
    return !ig.ignores(relativePath)
  })

  // Process files in parallel batches for better performance
  const BATCH_SIZE = 20
  const totalFiles = filesToProcess.length

  if (totalFiles === 0) {
    throw new Error(
      'No files to process after applying .gitignore rules. The folder may be empty or all files are ignored.'
    )
  }

  let processedFiles = 0

  onProgress?.('Reading files', processedFiles, totalFiles)

  try {
    for (let i = 0; i < filesToProcess.length; i += BATCH_SIZE) {
      const batch = filesToProcess.slice(i, i + BATCH_SIZE)

      // Read all files in batch in parallel
      const results = await Promise.all(
        batch.map(async file => {
          const filePath = isDropped
            ? file.webkitRelativePath.split('/').slice(1).join('/')
            : file.webkitRelativePath
          try {
            const fileContent = await file.arrayBuffer()
            return { filePath, fileContent }
          } catch (error) {
            throw new Error(
              `Failed to read file "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`
            )
          }
        })
      )

      // Add all files from batch to tar (must be sequential for tar format)
      for (const { filePath, fileContent } of results) {
        await tarWriter.addFile(filePath, new Uint8Array(fileContent))
        processedFiles++
        onProgress?.('Reading files', processedFiles, totalFiles)
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Failed to read files from the selected folder')
  }

  onProgress?.('Creating archive', totalFiles, totalFiles)
  let tarBlob: Blob
  try {
    tarBlob = await tarWriter.write()
  } catch (error) {
    throw new Error(
      `Failed to create tar archive: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }

  onProgress?.('Compressing', 0, 1)
  let tarArray: Uint8Array
  try {
    tarArray = new Uint8Array(await tarBlob.arrayBuffer())
  } catch (error) {
    throw new Error(
      `Failed to read tar archive (file may be too large): ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }

  // Use compression level 6 (faster than default 9, with minimal size difference)
  let gzippedTar: Uint8Array
  try {
    gzippedTar = pako.gzip(tarArray, { level: 6 })
  } catch (error) {
    throw new Error(
      `Failed to compress archive: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
  onProgress?.('Compressing', 1, 1)

  const originalFilename = `${rootFolder}.tar`
  const tarFileName = `${rootFolder}.tar.gz`
  const clientId = getDeviceId()
  const date = dayjs().format('YYYYMMDDHHmmss')
  const newTarFileName = `${clientId}-${date}___${tarFileName}`

  const newTarFile = new File([gzippedTar as unknown as ArrayBuffer], newTarFileName, {
    type: 'application/gzip'
  })

  // Store the original tar blob (before compression) in memory for download
  storeTarBlob(tarBlob, originalFilename)

  // Store the gzipped tar blob in memory for download
  const gzipBlob = new Blob([gzippedTar as unknown as ArrayBuffer], { type: 'application/gzip' })
  storeGzipBlob(gzipBlob, tarFileName)

  return {
    tarFile: newTarFile,
    tarBlob,
    originalFilename
  }
}

// Expose download functions globally
declare global {
  interface Window {
    downloadLastUpload: () => void
    downloadLastUploadGzip: () => void
  }
}

window.downloadLastUpload = downloadLastUpload
window.downloadLastUploadGzip = downloadLastUploadGzip
